import {
  activeItems,
  can,
  checkTicketCreate,
  findItem,
  type PaletteColor,
  TICKET_FIELD_MESSAGES,
  TITLE_MAX,
  type TicketFieldErrors,
} from '@hrms/shared'
import { type ReactNode, useId, useRef, useState } from 'react'
import { LuLock, LuUserRound } from 'react-icons/lu'
import { useCurrentUser } from '../../../auth/AuthProvider.tsx'
import {
  Button,
  Checkbox,
  Modal,
  Select,
  TextArea,
  TextInput,
  useToast,
} from '../../../components/ui/index.ts'
import { cn } from '../../../lib/cn.ts'
import { useConfig } from '../../../lib/config.ts'
import { hint } from '../../../lib/hint.ts'
import { AttachmentPicker, UploadProgress, useUploader } from './Attachments.tsx'
import { fieldErrorsOf, useDirectory, useRaiseTicket } from './api.ts'
import { closeRaiseTicket, type RaiseTicketOptions } from './raiseStore.ts'
import { useOpenTicket } from './useTicketParam.ts'

/** Choosing this category ticks "Keep this confidential" (PRD "Raising a ticket"). */
const AUTO_CONFIDENTIAL_CATEGORY = 'grievance'

function FormRow({
  label,
  htmlFor,
  help,
  error,
  errorId,
  children,
  as = 'div',
  tour,
}: {
  label: string
  htmlFor?: string
  help?: ReactNode
  error?: string | undefined
  errorId?: string
  children: ReactNode
  as?: 'div' | 'fieldset'
  tour?: string
}) {
  const Tag = as
  return (
    <Tag className="m-0 mb-4 min-w-0 border-0 p-0" data-tour={tour}>
      {as === 'fieldset' ? (
        <legend className="mb-1.5 p-0 text-[13.5px] font-semibold">{label}</legend>
      ) : (
        <label htmlFor={htmlFor} className="mb-1.5 block text-[13.5px] font-semibold">
          {label}
        </label>
      )}
      {children}
      {help && <p className="mt-1.5 text-[12.5px] text-muted">{help}</p>}
      {error && (
        <p id={errorId} className="mt-1.5 text-[12.5px] text-danger">
          {error}
        </p>
      )}
    </Tag>
  )
}

interface PillOption {
  id: string
  name: string
  color: PaletteColor
}

/** Radio pill group (DESIGN.md §6 "Radio pills"). */
function RadioPills({
  name,
  options,
  value,
  onChange,
  invalid,
  describedBy,
}: {
  name: string
  options: PillOption[]
  value: string
  onChange: (id: string) => void
  invalid?: boolean
  describedBy?: string
}) {
  return (
    <div
      role="radiogroup"
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className="flex flex-wrap gap-2"
    >
      {options.map((o) => (
        <label key={o.id} data-color={o.color} className="cursor-pointer">
          <input
            type="radio"
            name={name}
            value={o.id}
            checked={value === o.id}
            onChange={() => onChange(o.id)}
            className="peer sr-only"
          />
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border border-border-strong px-3 py-1.5 text-[13px] font-medium text-text transition-colors',
              'hover:bg-surface-2 peer-checked:border-cf peer-checked:bg-cb peer-checked:text-cf',
              'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent',
            )}
          >
            <span aria-hidden className="size-2 rounded-full bg-cf" />
            {o.name}
          </span>
        </label>
      ))}
    </div>
  )
}

const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`

/** "Raise a ticket" (DESIGN.md §3.6). Mounted by the top-bar button; open via `openRaiseTicket`. */
export function RaiseTicketModal({ options }: { options: RaiseTicketOptions }) {
  const user = useCurrentUser()
  const { data: config } = useConfig()
  // HR log tickets on someone's behalf: "Raised for" is required (DESIGN.md §3.6).
  const onBehalf = can(user, 'ticket.create.onBehalf')
  const directory = useDirectory(onBehalf)
  const employees = (directory.data?.people ?? []).filter((p) => p.role === 'member')
  const [raisedFor, setRaisedFor] = useState(options.raisedFor ?? '')
  const categories = activeItems(config?.categories ?? [])
  const priorities = activeItems(config?.priorities ?? [])

  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [priorityId, setPriorityId] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [confidential, setConfidential] = useState(false)
  const [errors, setErrors] = useState<TicketFieldErrors>({})
  const [files, setFiles] = useState<File[]>([])
  const uploader = useUploader(null)

  // Defaults once config is in: first category, second priority (Medium).
  const category = findItem(categories, categoryId) ?? categories[0]
  const priority = findItem(priorities, priorityId) ?? priorities[1] ?? priorities[0]

  const raise = useRaiseTicket()
  const busy = raise.isPending || uploader.uploading
  const toast = useToast()
  const openTicket = useOpenTicket()
  const ids = { title: useId(), description: useId(), err: useId(), raisedFor: useId() }
  const titleRef = useRef<HTMLInputElement>(null)
  const raisedForRef = useRef<HTMLSelectElement>(null)
  const descriptionRef = useRef<HTMLTextAreaElement>(null)

  const pickCategory = (id: string) => {
    setCategoryId(id)
    setErrors((e) => ({ ...e, categoryId: undefined }))
    if (id === AUTO_CONFIDENTIAL_CATEGORY) setConfidential(true)
  }

  const submit = async () => {
    const body = {
      title,
      description,
      categoryId: category?.id ?? '',
      priorityId: priority?.id ?? '',
      confidential,
      ...(onBehalf ? { raisedFor } : {}),
    }
    const found: TicketFieldErrors = checkTicketCreate(body)
    if (onBehalf && !raisedFor) found.raisedFor = TICKET_FIELD_MESSAGES.raisedFor
    setErrors(found)
    if (found.raisedFor) return raisedForRef.current?.focus()
    if (found.title) return titleRef.current?.focus()
    if (found.description) return descriptionRef.current?.focus()
    if (found.categoryId || found.priorityId) return
    if (busy) return
    try {
      const ticket = await raise.mutateAsync(body)
      // Files go up once the ticket exists; a failed upload keeps the ticket and says so.
      if (files.length > 0) {
        try {
          await uploader.upload(files, ticket.key)
          toast(
            `${ticket.key} raised with ${files.length === 1 ? '1 file' : `${files.length} files`}`,
          )
        } catch (err) {
          toast(
            `${ticket.key} raised, but the files weren’t attached: ${err instanceof Error ? err.message : 'upload failed'} Add them from the ticket.`,
            { duration: 9000 },
          )
        }
      } else toast(`${ticket.key} raised`)
      closeRaiseTicket()
      openTicket(ticket.key)
    } catch (err) {
      const fields = fieldErrorsOf(err)
      if (fields) setErrors(fields)
      else toast(err instanceof Error ? err.message : 'Couldn’t raise the ticket. Try again.')
    }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void submit()
    }
  }

  return (
    <Modal
      title="Raise a ticket"
      onClose={closeRaiseTicket}
      initialFocus={onBehalf ? raisedForRef : titleRef}
      footer={
        <>
          <Button onClick={closeRaiseTicket}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={busy || !config}
            {...hint('Ctrl or Cmd + Enter')}
          >
            {uploader.uploading ? 'Uploading…' : raise.isPending ? 'Raising…' : 'Raise ticket'}
          </Button>
        </>
      }
    >
      <form
        noValidate
        onKeyDown={onKeyDown}
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        {onBehalf && (
          <FormRow
            label="Raised for"
            htmlFor={ids.raisedFor}
            help="Log a ticket for someone who came to you in person or over email."
            error={errors.raisedFor}
            errorId={`${ids.err}-rf`}
          >
            <Select
              ref={raisedForRef}
              id={ids.raisedFor}
              value={raisedFor}
              aria-invalid={!!errors.raisedFor || undefined}
              aria-describedby={errors.raisedFor ? `${ids.err}-rf` : undefined}
              onChange={(e) => {
                setRaisedFor(e.target.value)
                if (errors.raisedFor) setErrors((x) => ({ ...x, raisedFor: undefined }))
              }}
              leading={<LuUserRound aria-hidden className="size-4 text-blue-fg" />}
              className={cn('h-auto py-2', errors.raisedFor && 'border-danger hover:border-danger')}
            >
              <option value="">Choose an employee</option>
              {employees.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.dept ? ` (${p.dept})` : ''}
                </option>
              ))}
            </Select>
          </FormRow>
        )}

        <FormRow
          as="fieldset"
          tour="raise-category"
          label="What's it about?"
          error={errors.categoryId}
          errorId={`${ids.err}-cat`}
        >
          <RadioPills
            name="category"
            options={categories}
            value={category?.id ?? ''}
            onChange={pickCategory}
            invalid={!!errors.categoryId}
          />
        </FormRow>

        <FormRow label="Title" htmlFor={ids.title} error={errors.title} errorId={`${ids.err}-t`}>
          <TextInput
            ref={titleRef}
            id={ids.title}
            value={title}
            maxLength={TITLE_MAX}
            placeholder="A short summary, like “Travel claim for Pune client visit”"
            invalid={!!errors.title}
            aria-describedby={errors.title ? `${ids.err}-t` : undefined}
            onChange={(e) => {
              setTitle(e.target.value)
              if (errors.title) setErrors((x) => ({ ...x, title: undefined }))
            }}
            className="h-auto py-2"
          />
        </FormRow>

        <FormRow
          label="Description"
          htmlFor={ids.description}
          error={errors.description}
          errorId={`${ids.err}-d`}
        >
          <TextArea
            ref={descriptionRef}
            id={ids.description}
            value={description}
            placeholder={category?.hint || 'Describe what you need help with.'}
            invalid={!!errors.description}
            aria-describedby={errors.description ? `${ids.err}-d` : undefined}
            onChange={(e) => {
              setDescription(e.target.value)
              if (errors.description) setErrors((x) => ({ ...x, description: undefined }))
            }}
            className="min-h-[120px]"
          />
        </FormRow>

        <FormRow
          as="fieldset"
          tour="raise-priority"
          label="How urgent is it?"
          error={errors.priorityId}
          help={
            priority &&
            `HR aims to resolve ${priority.name.toLowerCase()} tickets within ${days(priority.slaDays)}.${onBehalf ? '' : ' HR may adjust the priority after reviewing.'}`
          }
        >
          <RadioPills
            name="priority"
            options={priorities}
            value={priority?.id ?? ''}
            onChange={setPriorityId}
          />
        </FormRow>

        <FormRow as="fieldset" label="Attachments (optional)">
          <AttachmentPicker files={files} setFiles={setFiles} disabled={busy}>
            {uploader.progress !== null && (
              <UploadProgress progress={uploader.progress} count={files.length} />
            )}
          </AttachmentPicker>
        </FormRow>

        <div
          className="rounded-card border border-border px-3 py-2.5"
          data-tour="raise-confidential"
        >
          <Checkbox
            checked={confidential}
            onChange={(e) => setConfidential(e.target.checked)}
            className="items-start"
            label={
              <span className="block">
                <span className="flex items-center gap-1.5 font-semibold">
                  <LuLock aria-hidden className="size-3.5 text-amber-fg" />
                  Keep this confidential
                </span>
                <span className="block text-[12.5px] text-muted">
                  Only HR leads and the HR partner assigned to it will see this ticket.
                </span>
              </span>
            }
          />
        </div>
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  )
}
